import { TZDate } from '@date-fns/tz';

import { BYTES_GIGABYTE, formatBytes, ONE_DAY, parseAirDateToISO } from '@air/utilities';

import {
  type CreditUsageDisplayCategory,
  type CreditUsageState,
  type CreditUsageStats,
} from '~/components/CreditUsageMeter/types';

export const CREDIT_USAGE_CATEGORIES = [
  'generative',
  'enrichedStorage',
] as const satisfies CreditUsageDisplayCategory[];

export const CREDIT_USAGE_CATEGORY_COLOR_MAP = {
  generative: 'ads:bg-ads-sky-9 bg-sky-9',
  enrichedStorage: 'ads:bg-ads-sky-7 bg-sky-7',
  storage: 'ads:bg-ads-sky-7 bg-sky-7',
} as const satisfies Record<CreditUsageDisplayCategory, string>;

export type CreditUsageDisplayCategoryUsage = { gb?: number; credits: number };

// gb is `storage.gb` alone, NOT summed: `storage.gb` is already the basic+smart total,
// while `smartStorage.gb` is just the enriched subset — summing would double-count.
// credits are summed (distinct additive cost components).
export const getDisplayCategoryUsage = (
  used: CreditUsageStats['used'],
  category: CreditUsageDisplayCategory,
): CreditUsageDisplayCategoryUsage => {
  if (category === 'generative') {
    return { credits: used.generative.credits };
  }

  return {
    gb: used.storage.gb,
    credits: used.storage.credits + used.smartStorage.credits,
  };
};

// `enrichedStorage` and `storage` are one row and color, distinguished only by label:
// plain `storage` for workspaces with AI disabled (no AI add-ons enabled).
export const getStorageDisplayCategory = (hasAiAddons: boolean): CreditUsageDisplayCategory =>
  hasAiAddons ? 'enrichedStorage' : 'storage';

// On non-credit plans, hide storage rows until they accrue usage so the meter isn't
// cluttered with empty rows; generative always shows.
export const shouldRenderDisplayCategory = ({
  category,
  usage,
  isCreditModel,
}: {
  category: CreditUsageDisplayCategory;
  usage: CreditUsageDisplayCategoryUsage;
  isCreditModel: boolean;
}) => isCreditModel || !!usage.credits || category === 'generative';

const MIN_CREDIT_VALUE_FOR_COMPACT_FORMAT = 10_000;

export const formatCreditUsage = (usage: number) => {
  const formatOptions: Intl.NumberFormatOptions = {
    roundingMode: 'halfCeil',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };

  if (usage >= MIN_CREDIT_VALUE_FOR_COMPACT_FORMAT) {
    formatOptions.notation = 'compact';
    formatOptions.compactDisplay = 'short';
  }

  return new Intl.NumberFormat('en-US', formatOptions).format(usage);
};

export const formatStorageUsage = (gb: number) => {
  return formatBytes(gb * BYTES_GIGABYTE, { numberOfDecimals: 1 });
};

export const getPercentageUsed = (used: number, allocated: number | null) => {
  if (!allocated) {
    return 0;
  }

  // biome-ignore lint/style/noMagicNumbers: percentage calculation
  return (used / allocated) * 100;
};

export const getCreditUsageState = ({
  totalCredits,
  used,
}: {
  totalCredits: number;
  used: CreditUsageStats['used'];
}): CreditUsageState => {
  const percentage = getPercentageUsed(used.total, totalCredits);

  // biome-ignore lint/style/noMagicNumbers: percentage calculation
  if (percentage >= 100) {
    return 'depleted';
  }

  // Every plan warns once, when usage is nearly gone.
  // biome-ignore lint/style/noMagicNumbers: percentage calculation
  if (percentage >= 95) {
    return '95%';
  }

  return 'default';
};

/** Neutral meter chrome when credit overage is allowed — no warning or depleted UI, since usage is not blocked. */
export const getCreditUsageDisplayState = ({
  totalCredits,
  used,
  allowCreditOverage,
}: {
  totalCredits: number;
  used: CreditUsageStats['used'];
  allowCreditOverage: boolean;
}): CreditUsageState => {
  if (allowCreditOverage) {
    return 'default';
  }

  return getCreditUsageState({ totalCredits, used });
};

export const isCreditUsageDepletedDisplayState = (creditUsageState: CreditUsageState | undefined): boolean =>
  creditUsageState === 'depleted';

const CREDIT_USAGE_RESET_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const calendarDayUtc = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());

type CreditUsageResetDisplayParams = {
  nextCycleStartAt: string;
  now: Date;
  // Defaults to the runtime zone. Tests pass one explicitly because CI stays on UTC and ignores `process.env.TZ`.
  timeZone?: string;
};

// A UTC midnight is still the previous evening in the US. `date-fns` formats in the process zone, so the calendar day is read from a zoned date instead.
export const getCreditUsageResetDisplay = ({
  nextCycleStartAt,
  now,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}: CreditUsageResetDisplayParams) => {
  const resetDate = new TZDate(parseAirDateToISO(nextCycleStartAt), timeZone);
  const nowInZone = new TZDate(now, timeZone);
  const daysUntilReset = Math.round((calendarDayUtc(resetDate) - calendarDayUtc(nowInZone)) / ONE_DAY);

  return {
    daysUntilReset: Math.max(0, daysUntilReset),
    dateLabel: `${CREDIT_USAGE_RESET_MONTHS[resetDate.getMonth()]} ${resetDate.getDate()}`,
  };
};
